/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { reloadWindowToPath } from "../../Helpers/Utils";
import Select from "react-select";
import dayjs from "dayjs";
import Flatpickr from "react-flatpickr";
import { Row, Col, Button, Input, Table, Card, CardBody } from "reactstrap";

const FLATPICKR_CONFIG = {
    minDate: "today",
    dateFormat: "Y-m-d",
    closeOnSelect: false
};

const CreateQuotation = () => {
    const dispatch = useDispatch();
    const loading = useSelector(s => s.loading.loading);

    const [customers, setCustomers] = useState([]);
    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [items, setItems] = useState([]);

    const [form, setForm] = useState({
        customer_id: null,
        ref: "",
        valid_until: dayjs().add(7, 'day').format("YYYY-MM-DD"),
    });

    useEffect(() => {
        const loadCustomers = async () => {
            dispatch(loadingTrue());
            const res = await Handler({ method: "get", url: "/customer/list.php?status=active&limit=1000" });
            if (res?.success) {
                setCustomers(res.data.map(c => ({ value: c.id, label: c.company_name })));
            }
            dispatch(loadingFalse());
        };
        loadCustomers();
    }, [dispatch]);

    const handleCustomerChange = async (opt) => {
        const cId = opt ? opt.value : null;
        setForm(prev => ({ ...prev, customer_id: cId }));
        setItems([]);
        setVehicleOptions([]);

        if (cId) {
            dispatch(loadingTrue());
            const res = await Handler({
                method: "get",
                url: `/quotation/get_rate.php?customer_id=${cId}`
            });

            if (res?.success) {
                const formatted = res.data.map(v => ({
                    value: String(v.vehicle_id),
                    label: v.lorry_type,
                    rates: v // ⭐ Store the full JSON object per vehicle
                }));
                setVehicleOptions(formatted);
            }
            dispatch(loadingFalse());
        }
    };

    const handleVehicleSelect = (selectedOpts) => {
        const currentSelection = selectedOpts || [];
        const updatedItems = currentSelection.map(opt => {
            const existing = items.find(it => String(it.vehicle_id) === String(opt.value));
            if (existing) return existing;

            return {
                vehicle_id: String(opt.value),
                vehicle_label: opt.label,
                item_ref: "",
                rates: opt.rates, // ⭐ Passing rates from JSON to this item
                normal_hours: 0,
                normal_hours_ot: 0,
                after_office_hours: 0,
                offz_hours_ot: 0,
                holiday_hours: 0
            };
        });
        setItems(updatedItems);
    };

    const handleItemEntry = (index, field, value) => {
        const updated = [...items];
        const val = (value === "" || value === null) ? 0 : value;
        updated[index][field] = field === "item_ref" ? val : Number(val);
        setItems(updated);
    };

    const calculateLineTotal = (item) => {
        const r = item.rates || {};

        // ⭐ KEY FIX: Mapping your JSON keys correctly to the calculation
        const total =
            (Number(item.normal_hours || 0) * Number(r.normal_hrs || 0)) +
            (Number(item.normal_hours_ot || 0) * Number(r.normal_hrs_ot || 0)) +
            (Number(item.after_office_hours || 0) * Number(r.after_office_hrs || 0)) +
            (Number(item.offz_hours_ot || 0) * Number(r.offz_hrs_ot || 0)) +
            (Number(item.holiday_hours || 0) * Number(r.holiday || 0));

        return Number(total).toFixed(2);
    };

    const calculateGrandTotal = () => {
        return items.reduce((acc, it) => acc + Number(calculateLineTotal(it)), 0).toFixed(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.customer_id || items.length === 0) return toast.error("Please fill all required fields");

        dispatch(loadingTrue());

        const payload = {
            customer_id: form.customer_id,
            ref: form.ref,
            valid_until: dayjs(form.valid_until).format("YYYY-MM-DD"),
            items: items.map(it => ({
                vehicle_id: it.vehicle_id,
                item_ref: it.item_ref,
                normal_hours: it.rates.normal_hrs,
                normal_hours_ot: it.rates.normal_hrs_ot,
                after_office_hours: it.rates.after_office_hrs,
                offz_hours_ot: it.rates.offz_hrs_ot,
                holiday_hours: it.rates.holiday,
                line_total: calculateLineTotal(it),
                // ⭐ Passing fixed rates to backend just in case
                rates: {
                    normal: it.rates.normal_hrs,
                    normal_ot: it.rates.normal_hrs_ot,
                    after_office: it.rates.after_office_hrs,
                    offz_ot: it.rates.offz_hrs_ot,
                    holiday: it.rates.holiday
                }
            }))
        };

        const res = await Handler({ method: "post", url: "/quotation/create.php", data: payload });

        if (res?.success || res?.status === true) {
            toast.success("Quotation Generated Successfully!");
            setTimeout(() => reloadWindowToPath("/quotations"), 1500);
        } else {
            toast.error(res?.message || "Failed to create");
        }
        dispatch(loadingFalse());
    };

    return (
        <div className="page-card p-4 shadow-sm border-0 rounded-4 bg-white">
            <h4 className="fw-bold text-dark mb-4 border-bottom pb-2">Create Quotation</h4>

            <form onSubmit={handleSubmit}>
                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <label className="form-label small text-muted fw-bold">Customer *</label>
                        <Select options={customers} onChange={handleCustomerChange} placeholder="Select customer" isClearable />
                    </Col>
                    <Col md={3}>
                        <label className="form-label small text-muted fw-bold">Main Reference</label>
                        <Input placeholder="General Ref" value={form.ref} onChange={e => setForm({ ...form, ref: e.target.value })} />
                    </Col>
                    <Col md={3}>
                        <label className="form-label small text-muted fw-bold">Select Vehicles *</label>
                        <Select
                            isMulti
                            options={vehicleOptions}
                            value={vehicleOptions.filter(o => items.some(it => it.vehicle_id === o.value))}
                            onChange={handleVehicleSelect}
                            isDisabled={!form.customer_id}
                            closeMenuOnSelect={false}
                        />
                    </Col>
                    <Col md={3}>
                        <label className="form-label small text-muted fw-bold">Valid Until *</label>
                        <Flatpickr className="form-control" value={form.valid_until} onChange={([d]) => setForm({ ...form, valid_until: d })} options={FLATPICKR_CONFIG} />
                    </Col>
                </Row>

                {/* --- Rate Reference Table --- */}
                {items.length > 0 && (
                    <div className="mb-4">
                        <h6 className="fw-bold text-secondary mb-3">Unit Rates (From Customer Profile)</h6>
                        <div className="table-responsive border rounded-3">
                            <Table className="mb-0 text-center" size="sm" bordered striped>
                                <thead className="bg-dark text-white">
                                    <tr>
                                        <th>Vehicle</th>
                                        <th>Normal</th>
                                        <th>Normal OT</th>
                                        <th>After Offz</th>
                                        <th>Offz OT</th>
                                        <th>Holiday</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(it => (
                                        <tr key={it.vehicle_id}>
                                            <td className="fw-bold">{it.vehicle_label}</td>
                                            <td>${it.rates?.normal_hrs}</td>
                                            <td>${it.rates?.normal_hrs_ot}</td>
                                            <td>${it.rates?.after_office_hrs}</td>
                                            <td>${it.rates?.offz_hrs_ot}</td>
                                            <td>${it.rates?.holiday}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}

                {/* --- Vehicle Cards --- */}
                {/* {items?.map((item, index) => (
                    <Card key={item.vehicle_id} className="mb-3 border-0 shadow-sm bg-light rounded-4">
                        <CardBody>
                            <div className="d-flex justify-content-between mb-3 align-items-center">
                                <h6 className="fw-bold text-primary mb-0">{item.vehicle_label}</h6>
                                <span className="badge bg-success px-3 py-2 fs-6">Total: ${calculateLineTotal(item)}</span>
                            </div>
                            <Row className="g-2">
                                <Col md={2}><label className="extra-small fw-bold">Ref</label><Input type="text" value={item.item_ref || ""} onChange={e => handleItemEntry(index, "item_ref", e.target.value)} /></Col>
                                <Col md={2}><label className="extra-small fw-bold">Normal</label><Input type="number" step="any" value={item.normal_hours || ""} onChange={e => handleItemEntry(index, "normal_hours", e.target.value)} /></Col>
                                <Col md={2}><label className="extra-small fw-bold">N-OT</label><Input type="number" step="any" value={item.normal_hours_ot || ""} onChange={e => handleItemEntry(index, "normal_hours_ot", e.target.value)} /></Col>
                                <Col md={2}><label className="extra-small fw-bold">After</label><Input type="number" step="any" value={item.after_office_hours || ""} onChange={e => handleItemEntry(index, "after_office_hours", e.target.value)} /></Col>
                                <Col md={2}><label className="extra-small fw-bold">O-OT</label><Input type="number" step="any" value={item.offz_hours_ot || ""} onChange={e => handleItemEntry(index, "offz_hours_ot", e.target.value)} /></Col>
                                <Col md={2}><label className="extra-small fw-bold">Holiday</label><Input type="number" step="any" value={item.holiday_hours || ""} onChange={e => handleItemEntry(index, "holiday_hours", e.target.value)} /></Col>
                            </Row>
                        </CardBody>
                    </Card>
                ))} */}

                {items.length > 0 && (
                    <div className="text-end pt-4 mt-3 border-top">
                        {/* <h2 className="fw-bold text-success mb-3">Grand Total: ${calculateGrandTotal()}</h2> */}
                        <Button color="success" size="lg" className="px-5 rounded-pill shadow" type="submit" disabled={loading}>
                            {loading ? "Generating..." : "Generate Quotation"}
                        </Button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default CreateQuotation;